<template>
  <base-modal
    hide-footer
    :size="'lg'"
    :modal-id="'history-report'"
    @hidden="$emit('onCloseModal')"
  >
    <div class="px-2 pt-2 d-flex justify-content-between align-item-center">
      <div class="head-label">
        <span>{{ generateObject.reportName || "Report" }}</span>
        <div>Update every minute</div>
      </div>
    </div>
    <div class="px-2 pt-2">
      <b-row>
        <b-col
          class="col-6 d-flex align-items-end"
          v-if="statusOption.length > 1"
        >
          <div>
            <label class="font-medium-4">Select Report</label>
            <v-select
              v-model="form.status"
              class="v-select-filter v-select-filter-custom mr-2 bg-dropdown"
              :class="form.status ? 'v-select-filter-selected' : null"
              placeholder="Select Report"
              :options="statusOption"
              :reduce="(option) => option.value"
              @input="onSelectReportCategory({ status: $event })"
              :clearable="false"
            />
          </div>
        </b-col>
        <b-col class="d-flex align-items-end justify-content-end">
          <b-row>
            <b-col>
              <div class="d-flex align-items-end justify-content-end">
                <b-button
                  :variant="'primary'"
                  @click="onOpenGenerateModal"
                  :disabled="loading.btnReport"
                >
                  <div class="d-flex align-items-center w-nowrap">
                    <span> {{ "Generate Report" }}</span>
                  </div>
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <div class="px-1 py-2">
      <overlay-loading :loading="loading.tableData">
        <template #content>
          <b-table
            :key="`key-${keyChange}`"
            responsive
            bordered
            borderless
            sticky-header
            show-empty
            no-local-sorting
            :fields="fields"
            :items="historyReportList"
            :busy="loading.tableData"
            class="default-static-table"
            :class="{ 'loading-table': loading.tableData }"
          >
            <template #cell(filter)="data">
              <span v-if="data.item.filter">
                {{ data.item.filter }}
              </span>
              <span v-else>{{ "-" }}</span>
            </template>
            <template #cell(updatedtime)="data">
              <span v-if="data.item.updatedtime">
                <!-- {{ resolveDateWithSeperator(data.item.updatedtime, true) }} -->
                {{ resolveDate(data.item.updatedtime, "dd/MM/yyyy") }}
              </span>
              <span v-else>{{ "-" }}</span>
            </template>
            <template #cell(date_time)="data">
              <span v-if="data.item.querystarttime && data.item.queryendtime">
                {{
                  resolveDateWithComparator(
                    data.item.querystarttime,
                    data.item.queryendtime
                  )
                }}
              </span>
              <span v-else>{{ "-" }}</span>
            </template>
            <template #cell(progress)="data">
              <span>{{ `${data.item.progress}%` }}</span>
            </template>
            <template #cell(status)="data">
              <div class="d-flex align-items-center">
                <div
                  class="status-icon mr-2"
                  :class="
                    data.item.status == 4
                      ? 'complete'
                      : data.item.status == 3
                      ? 'failed'
                      : 'pending'
                  "
                />
                <div>{{ data.item.status_desc }}</div>
              </div>
            </template>
            <template #cell(actions)="data">
              <div class="d-flex align-items-center">
                <button
                  v-b-tooltip.hover
                  title="Export .CSV"
                  :disabled="!data.item.fileurl"
                  :class="data.item.fileurl ? 'active' : 'disabled'"
                  class="download-button"
                  @click="onClickDownload(data.item.report_job_id)"
                >
                  <div
                    class="d-flex flex-column justify-content-center align-items-center"
                  >
                    <download-icon />
                  </div>
                </button>
                <a
                  :ref="`download-${data.item.report_job_id}`"
                  :href="data.item.fileurl"
                  download
                />
                <button
                  v-b-tooltip.hover
                  title="Delete"
                  class="default-btn-action trash-button"
                  @click="onClickDelete(data.item)"
                >
                  <b-spinner v-if="data.item.loading" small variant="primary" />
                  <trash-icon v-else />
                </button>
              </div>
            </template>
            <template #empty>
              <card-no-item :text-no-img="'Item Not Found'" />
            </template>
          </b-table>
        </template>
      </overlay-loading>
      <pagination-with-reorder
        :pagination="pagination"
        @entityChange="onFilterEntity"
        @pageChange="onPageChange"
      />
    </div>
    <GenerateReportModal
      v-if="showModal.generate"
      :customDataObj="{
        statusOption: statusOption,
        formStatus: form.status,
        groupId: GroupId,
        SurveyGroupId: SurveyGroupId,
      }"
      @onCloseGenerateReportModal="closeGenerateModal"
      @generateDefaultReport="generateDefaultReport"
      @onSelectReportCategory="onSelectReportCategory"
    />
  </base-modal>
</template>

<script>
import PaginationWithReorder from "@/views/components/pagination/PaginationWithReorder.vue";
import DownloadIcon from "@/@core/components/svg/common/Download.vue";
import TrashIcon from "@/@core/components/svg/common/Trash.vue";
import CardNoItem from "@core/components/card-no-item/CardNoItem.vue";
import vSelect from "vue-select";

import useHistoryReportModal from "./hooks/useHistoryReportModal";
import OverlayLoading from "@/views/components/loading/OverlayLoading.vue";
import flatPickr from "vue-flatpickr-component";
import CalendarPickerIcon from "@/@core/components/svg/common/CalendarPicker.vue";
import ClockPickerIcon from "@/@core/components/svg/common/ClockPicker.vue";
import GenerateReportModal from "@/views/pages/report/history-report/components/GenerateReportModal.vue";

export default {
  components: {
    PaginationWithReorder,
    DownloadIcon,
    TrashIcon,
    CardNoItem,
    OverlayLoading,
    vSelect,
    flatPickr,
    CalendarPickerIcon,
    ClockPickerIcon,
    GenerateReportModal,
  },
  props: {
    moduleTypeKey: { type: String, default: "bcrm_report" },
    moduleKey: { type: String, default: "" },
    GroupId: { type: Number, default: null },
    SurveyGroupId: { type: Number, default: null },
  },
  setup(props) {
    const {
      fields,
      historyReportList,
      loading,
      pagination,
      keyChange,
      resolveDateWithSeperator,
      resolveDateWithComparator,
      resolveDate,
      onPageChange,
      onFilterEntity,
      onClickDownload,
      onClickDelete,
      generateDefaultReport,
      generateObject,
      form,
      statusOption,
      validFrom,
      validThrough,
      flatpickrDateConfig,
      flatpickrTimeConfig,
      onOpenValidFromDatePicker,
      onOpenValidFromTimePicker,
      onOpenValidThroughTimePicker,
      onSelectValidFromDate,
      onSelectValidFromTimePicker,
      resolveDateString,
      onOpenGenerateModal,
      closeGenerateModal,
      showModal,
      onSelectReportCategory,
    } = useHistoryReportModal(props);

    return {
      fields,
      historyReportList,
      loading,
      pagination,
      keyChange,
      resolveDateWithSeperator,
      resolveDateWithComparator,
      resolveDate,
      onPageChange,
      onFilterEntity,
      onClickDownload,
      onClickDelete,
      generateDefaultReport,
      generateObject,
      form,
      statusOption,
      validFrom,
      validThrough,
      flatpickrDateConfig,
      flatpickrTimeConfig,
      onOpenValidFromDatePicker,
      onOpenValidFromTimePicker,
      onOpenValidThroughTimePicker,
      onSelectValidFromDate,
      onSelectValidFromTimePicker,
      resolveDateString,
      onOpenGenerateModal,
      closeGenerateModal,
      showModal,
      onSelectReportCategory,
    };
  },
};
</script>

<style scoped>
#history-report .v-select-filter {
  min-width: 335px !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style>
@import "~@/assets/css/pages/report/history-report-modal.css";
@import "~@/assets/css/pages/report/history-report.css";
@import "~@/assets/css/pages/default-static-table.css";
@import "~@/assets/css/override-styles/v-select.css";
@import "~@/assets/css/override-styles/dynamic-fields.css";
@import "~@/assets/css/pages/data-management.css";
</style>
